import packageInfo from "../../../package.json";

const LIBRARY_STORAGE_VERSION_KEY = "__mystand_library__:storage-version";
const LIBRARY_STORAGE_RESET_APPLIED_KEY = "__mystand_library__:storage-reset-applied-version";
const LIBRARY_STORAGE_RELOAD_KEY = "__mystand_library__:reload-version";

const LIBRARY_STORAGE_EXACT_KEYS = new Set([
  "graphicFilters",
  "graphicsSorting",
  "flooringFilters",
  "flooringSorting",
  "flooringsSorting",
  "usersFilters",
  "usersFiltersExtra",
  "usersFiltersMore",
  "usersSorting",
  "supplierFilters",
  "suppliersSorting",
]);

const LIBRARY_STORAGE_PREFIXES = [
  "equipmentFilters_groupID-",
  "equipmentsSorting_groupID-",
  "standFilters_groupID-",
  "standFiltersExtra_groupID-",
  "standFiltersMore_groupID-",
  "standsSorting_groupID-",
];

const isLibraryStorageKey = (key: string): boolean =>
  LIBRARY_STORAGE_EXACT_KEYS.has(key)
  || LIBRARY_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearMatchingStorage = (storage: Storage | undefined) => {
  if (!storage) {
    return;
  }

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);

    if (key && isLibraryStorageKey(key)) {
      storage.removeItem(key);
    }
  }
};

const hasMatchingStorage = (storage: Storage | undefined): boolean => {
  if (!storage) {
    return false;
  }

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (key && isLibraryStorageKey(key)) {
      return true;
    }
  }

  return false;
};

const safeStorageOperation = <T>(operation: () => T, fallback: T): T => {
  try {
    return operation();
  } catch (error) {
    return fallback;
  }
};

export const getLibraryBuildVersion = (): string => packageInfo.version;

export const clearLibraryClientStorage = () => {
  if (typeof window === "undefined") {
    return;
  }

  safeStorageOperation(() => clearMatchingStorage(window.localStorage), undefined);
  safeStorageOperation(() => clearMatchingStorage(window.sessionStorage), undefined);
};

export const applyVersionedLibraryStorageReset = (
  currentVersion: string = getLibraryBuildVersion()
): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const previousVersion = safeStorageOperation(
    () => window.localStorage.getItem(LIBRARY_STORAGE_VERSION_KEY),
    null
  );
  const resetAppliedVersion = safeStorageOperation(
    () => window.localStorage.getItem(LIBRARY_STORAGE_RESET_APPLIED_KEY),
    null
  );
  const hasLegacyLibraryStorage = safeStorageOperation(
    () => hasMatchingStorage(window.localStorage) || hasMatchingStorage(window.sessionStorage),
    false
  );

  if (
    previousVersion === currentVersion
    && (resetAppliedVersion === currentVersion || !hasLegacyLibraryStorage)
  ) {
    safeStorageOperation(() => {
      window.localStorage.setItem(LIBRARY_STORAGE_RESET_APPLIED_KEY, currentVersion);
      return undefined;
    }, undefined);

    safeStorageOperation(() => {
      window.sessionStorage.removeItem(LIBRARY_STORAGE_RELOAD_KEY);
      return undefined;
    }, undefined);

    return false;
  }

  safeStorageOperation(() => {
    window.localStorage.setItem(LIBRARY_STORAGE_VERSION_KEY, currentVersion);
    return undefined;
  }, undefined);

  if (!hasLegacyLibraryStorage) {
    safeStorageOperation(() => {
      window.localStorage.setItem(LIBRARY_STORAGE_RESET_APPLIED_KEY, currentVersion);
      return undefined;
    }, undefined);

    return false;
  }

  clearLibraryClientStorage();

  safeStorageOperation(() => {
    window.localStorage.setItem(LIBRARY_STORAGE_RESET_APPLIED_KEY, currentVersion);
    return undefined;
  }, undefined);

  const reloadToken = `${previousVersion || "legacy"}->${currentVersion}`;
  const hasReloadedForVersion = safeStorageOperation(
    () => window.sessionStorage.getItem(LIBRARY_STORAGE_RELOAD_KEY) === reloadToken,
    false
  );

  if (!hasReloadedForVersion) {
    safeStorageOperation(() => {
      window.sessionStorage.setItem(LIBRARY_STORAGE_RELOAD_KEY, reloadToken);
      return undefined;
    }, undefined);

    window.location.reload();
    return true;
  }

  safeStorageOperation(() => {
    window.sessionStorage.removeItem(LIBRARY_STORAGE_RELOAD_KEY);
    return undefined;
  }, undefined);

  return false;
};
